import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import Icon from '../../../components/ui/Icon'
import { Tabs } from '../../../components/tabs/Tabs'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { SearchInput } from '../../../components/input/searchInput/SearchInput'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { isFeatureEnabled } from '../../../constants/featureFlags'
import {
  HELP_AUDIENCE_ROLES,
  HELP_SECTIONS,
  HelpAudience,
  HelpSection,
  HelpTopic,
} from '../../../constants/help'

const ALL = 'all'

type TopicText = {
  title: string
  summary: string
  steps: string[]
  tips: string[]
}

type ResolvedTopic = HelpTopic & {
  text: TopicText
  crumbLabels: string[]
  /** Every text of the topic, folded for searching. */
  haystack: string
}

type ResolvedSection = Omit<HelpSection, 'topics'> & {
  title: string
  topics: ResolvedTopic[]
}

/** Lower-cased and without diacritics, so "anulare" finds "Anulează" and "sesiune" finds "sesiunea". */
const fold = (char: string) =>
  char.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const normalize = (text: string) => Array.from(text).map(fold).join('')

const searchTerms = (query: string) => normalize(query).split(/\s+/).filter(Boolean)

/** Marks every occurrence of the search terms, ignoring case and diacritics. */
const Highlight = ({ text, terms }: { text: string; terms: string[] }) => {
  if (terms.length === 0) {
    return <>{text}</>
  }

  const chars = Array.from(text)
  let folded = ''
  const origin: number[] = []
  chars.forEach((char, index) => {
    Array.from(fold(char)).forEach(foldedChar => {
      folded += foldedChar
      origin.push(index)
    })
  })

  const marked = new Array<boolean>(chars.length).fill(false)
  terms.forEach(term => {
    let from = folded.indexOf(term)
    while (from !== -1) {
      for (let i = from; i < from + term.length; i++) {
        marked[origin[i]] = true
      }
      from = folded.indexOf(term, from + term.length)
    }
  })

  const runs: { text: string; marked: boolean }[] = []
  chars.forEach((char, index) => {
    const last = runs[runs.length - 1]
    if (last && last.marked === marked[index]) {
      last.text += char
    } else {
      runs.push({ text: char, marked: marked[index] })
    }
  })

  return (
    <>
      {runs.map((run, index) =>
        run.marked ? <mark key={index}>{run.text}</mark> : <Fragment key={index}>{run.text}</Fragment>
      )}
    </>
  )
}

/**
 * The help center: how to do everything the signed-in user's roles allow, searchable. Players see only the player
 * guides; managers also see operations and setup, administrators everything.
 */
const Help = () => {
  const { t, i18n } = useTranslation(['help', 'nav', 'account'])
  const navigate = useNavigate()
  const { hash } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { roles, showBanner } = useApp()
  const query = searchParams.get('q') ?? ''
  const [audience, setAudience] = useState<string>(ALL)
  const [open, setOpen] = useState<Set<string>>(() => new Set())

  const audiences = useMemo(
    () =>
      Object.values(HelpAudience).filter(value => {
        const allowed = HELP_AUDIENCE_ROLES[value]
        return allowed === null || allowed.some(role => roles.includes(role))
      }),
    [roles]
  )

  const sections = useMemo<ResolvedSection[]>(
    () =>
      HELP_SECTIONS.filter(
        section => audiences.includes(section.audience) && (!section.flag || isFeatureEnabled(section.flag))
      ).map(section => {
        const title = t(`sections.${section.id}`)
        return {
          ...section,
          title,
          topics: section.topics
            .filter(topic => !topic.flag || isFeatureEnabled(topic.flag))
            .map(topic => {
              const text = t(`topics.${topic.id}`, { returnObjects: true }) as unknown as TopicText
              const crumbLabels = (topic.crumbs ?? []).map(key => t(key))
              return {
                ...topic,
                text,
                crumbLabels,
                haystack: normalize(
                  [title, text.title, text.summary, ...text.steps, ...text.tips, ...crumbLabels].join(' ')
                ),
              }
            }),
        }
      }),
    // The texts follow the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [audiences, i18n.language]
  )

  const terms = useMemo(() => searchTerms(query), [query])
  const searching = terms.length > 0

  const visibleSections = useMemo(
    () =>
      sections
        .filter(section => audience === ALL || section.audience === audience)
        .map(section => ({
          ...section,
          topics: searching
            ? section.topics.filter(topic => terms.every(term => topic.haystack.includes(term)))
            : section.topics,
        }))
        .filter(section => section.topics.length > 0),
    [sections, audience, searching, terms]
  )

  const totalTopics = sections.reduce((sum, section) => sum + section.topics.length, 0)
  const matchCount = visibleSections.reduce((sum, section) => sum + section.topics.length, 0)
  const visibleTopicIds = visibleSections.flatMap(section => section.topics.map(topic => topic.id))
  const allOpen = visibleTopicIds.length > 0 && visibleTopicIds.every(id => open.has(id))

  useEffect(() => {
    // A shared link (#topic-…) opens that topic and scrolls to it.
    const id = hash.startsWith('#topic-') ? hash.slice('#topic-'.length) : null
    if (!id || !sections.some(section => section.topics.some(topic => topic.id === id))) {
      return
    }
    setOpen(current => new Set(current).add(id))
    window.requestAnimationFrame(() =>
      document.getElementById(`topic-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])

  const setQuery = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set('q', value)
    } else {
      next.delete('q')
    }
    setSearchParams(next, { replace: true })
  }

  const toggle = (id: string) =>
    setOpen(current => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(visibleTopicIds))

  const copyLink = async (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#topic-${id}`
    try {
      await navigator.clipboard.writeText(url)
      showBanner({ kind: BannerKind.OK, msg: t('page.link-copied') })
    } catch {
      window.location.hash = `topic-${id}`
    }
  }

  const scrollToSection = (id: string) =>
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const renderTopic = (topic: ResolvedTopic) => {
    // While searching every match is shown open, so the matching step is visible.
    const expanded = searching || open.has(topic.id)
    const bodyId = `topic-${topic.id}-body`

    return (
      <article key={topic.id} id={`topic-${topic.id}`} className={`help-topic${expanded ? ' open' : ''}`}>
        <button
          type="button"
          className="help-topic-head"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => !searching && toggle(topic.id)}
        >
          <span className="help-topic-text">
            <span className="help-topic-title">
              <Highlight text={topic.text.title} terms={terms} />
            </span>
            <span className="help-topic-summary">
              <Highlight text={topic.text.summary} terms={terms} />
            </span>
          </span>
          {!searching && <Icon name="chev" size={16} className="help-topic-chev" />}
        </button>

        {expanded && (
          <div className="help-topic-body" id={bodyId}>
            {topic.crumbLabels.length > 0 && (
              <div className="help-where">
                <span className="eyebrow">{t('page.where')}</span>
                <ol className="help-crumbs">
                  {topic.crumbLabels.map((label, index) => (
                    <li key={index}>
                      <Highlight text={label} terms={terms} />
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="eyebrow mb-8">{t('page.steps')}</div>
            <ol className="help-steps">
              {topic.text.steps.map((step, index) => (
                <li key={index}>
                  <Highlight text={step} terms={terms} />
                </li>
              ))}
            </ol>

            {topic.text.tips.length > 0 && (
              <div className="help-tips">
                <Icon name="info" size={16} />
                <div>
                  <strong>{t('page.good-to-know')}</strong>
                  <ul>
                    {topic.text.tips.map((tip, index) => (
                      <li key={index}>
                        <Highlight text={tip} terms={terms} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="help-topic-actions">
              {topic.path && (
                <Button
                  label={t('page.go-to-page')}
                  icon={<Icon name="arrowR" size={14} />}
                  iconPosition="right"
                  size="sm"
                  onClick={() => navigate(topic.path as string)}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                label={t('page.copy-link')}
                onClick={() => void copyLink(topic.id)}
              />
            </div>
          </div>
        )}
      </article>
    )
  }

  return (
    <div className="help-page">
      <PageHeader
        title={{
          eyebrow: t('page.eyebrow', { count: totalTopics }),
          h1: t('page.title'),
        }}
        sub={t('page.sub')}
        actions={
          searching || visibleTopicIds.length === 0
            ? undefined
            : [
                <Button
                  key="toggle-all"
                  variant="ghost"
                  size="md"
                  label={allOpen ? t('page.collapse-all') : t('page.expand-all')}
                  icon={<Icon name={allOpen ? 'chevUp' : 'chev'} size={14} />}
                  onClick={toggleAll}
                />,
              ]
        }
      />

      <div className="help-toolbar">
        <div className="help-search">
          <SearchInput
            iconName="search"
            shortcutKey="k"
            value={query}
            placeholder={t('page.search-placeholder')}
            aria-label={t('page.search-label')}
            onChange={event => setQuery(event.target.value)}
          />
          {query && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="help-search-clear"
              label={t('page.clear-search')}
              icon={<Icon name="x" size={14} />}
              onClick={() => setQuery('')}
            />
          )}
        </div>

        {audiences.length > 1 && (
          <Tabs
            tab={audience}
            tabs={[
              { value: ALL, label: t('audience.all') },
              ...audiences.map(value => ({ value, label: t(`audience.${value}`) })),
            ]}
            onChange={setAudience}
          />
        )}
      </div>

      {searching && matchCount > 0 && (
        <p className="help-results muted" aria-live="polite">
          {t('page.results', { count: matchCount, query: query.trim() })}
        </p>
      )}

      {visibleSections.length === 0 ? (
        <div className="ph-card help-empty" aria-live="polite">
          <Icon name="search" size={22} />
          <h3>{t('page.empty-title', { query: query.trim() })}</h3>
          <p className="muted">{t('page.empty-text')}</p>
          <Button variant="ghost" label={t('page.clear-search')} onClick={() => setQuery('')} />
        </div>
      ) : (
        <div className="help-layout">
          <nav className="help-contents" aria-label={t('page.contents')}>
            <div className="eyebrow">{t('page.contents')}</div>
            <ul>
              {visibleSections.map(section => (
                <li key={section.id}>
                  <button type="button" onClick={() => scrollToSection(section.id)}>
                    <Icon name={section.icon} size={15} />
                    <span>{section.title}</span>
                    <span className="count">{section.topics.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="help-sections">
            {visibleSections.map(section => (
              <section key={section.id} id={`section-${section.id}`} className="help-section">
                <header className="help-section-head">
                  <span className="help-section-icon">
                    <Icon name={section.icon} size={18} />
                  </span>
                  <h2>{section.title}</h2>
                  {audiences.length > 1 && (
                    <span className={`help-audience help-audience--${section.audience}`}>
                      {t(`audience.${section.audience}`)}
                    </span>
                  )}
                </header>
                <div className="help-topics">{section.topics.map(renderTopic)}</div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Help
